/**
 * Browser-owned gateway session lane.
 *
 * The PrestaShop PHP runtime must not call the external gateway during
 * bootstrap. The iframe reads local identity through the admin bridge, calls
 * the gateway from the browser, persists fresh credentials back through the
 * bridge, then signs a login challenge through the plugin.
 */

import { GatewaySessionErrorCode } from './gatewaySessionErrors';
import { getLocalizedMessage } from '../../../util/useLocalizedNotifications';

const PLACEHOLDER_HOSTS = new Set([
  'example.com',
  'example.net',
  'example.org',
  'example.invalid',
  'gateway.example.com',
  'gw.example.com',
]);

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeBaseUrl(value) {
  if (!isNonEmptyString(value)) return '';
  let parsed;
  try {
    parsed = new URL(value.trim());
  } catch (_e) {
    return '';
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
  const hostname = parsed.hostname.toLowerCase();
  if (PLACEHOLDER_HOSTS.has(hostname)) return '';
  if (hostname.endsWith('.example.com')
    || hostname.endsWith('.example.net')
    || hostname.endsWith('.example.org')) {
    return '';
  }
  if (hostname === 'host.docker.internal' || hostname === 'kea-fullchain-gateway') {
    parsed.hostname = 'localhost';
  }
  parsed.pathname = parsed.pathname.replace(/\/+$/, '');
  return parsed.toString().replace(/\/+$/, '');
}

function readFallbackBaseUrl() {
  if (typeof window === 'undefined') return '';
  const candidates = [
    window.KEA_PRO_GATEWAY_URL,
    window.KEA_GATEWAY_BASE_URL,
    window.__KEA_GATEWAY_BASE_URL__,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = normalizeBaseUrl(candidates[i]);
    if (normalized) return normalized;
  }
  return '';
}

function unwrapBridgeSuccess(response, action) {
  if (!response || typeof response !== 'object') return {};
  if (response.result === 'error' || (response.error && response.success !== true)) {
    return null;
  }
  if (action === 'gateway.identity.get' && response.identity && typeof response.identity === 'object') {
    return response.identity;
  }
  if (response.data && typeof response.data === 'object') return response.data;
  if (response.payload && typeof response.payload === 'object') return response.payload;
  return response;
}

function normalizeBridgeError(err, fallbackCode = GatewaySessionErrorCode.BRIDGE_ERROR) {
  const code = isNonEmptyString(err?.code) ? err.code : fallbackCode;
  const message = err?.error || err?.message || getLocalizedMessage('shell.bridge.communicationFailed');
  if (/installation not found/i.test(message)) {
    return { code: GatewaySessionErrorCode.INSTALLATION_NOT_FOUND, message };
  }
  if (/credentials.*not configured|secret.*missing|token.*missing/i.test(message)) {
    return { code: GatewaySessionErrorCode.CREDENTIALS_MISSING, message };
  }
  if (/not configured|unconfigured/i.test(message)) {
    return { code: GatewaySessionErrorCode.GATEWAY_SESSION_UNAVAILABLE, message };
  }
  return { code, message };
}

async function callBridge(bridge, action, payload = {}) {
  if (!bridge || typeof bridge.sendMessageWithResponse !== 'function') {
    return {
      ok: false,
      error: {
        code: GatewaySessionErrorCode.BRIDGE_ERROR,
        message: 'Bridge client is not available',
      },
    };
  }

  try {
    const response = await bridge.sendMessageWithResponse(action, payload);
    const data = unwrapBridgeSuccess(response, action);
    if (data === null) {
      return {
        ok: false,
        error: normalizeBridgeError(response, GatewaySessionErrorCode.GATEWAY_SESSION_ERROR),
      };
    }
    return { ok: true, data };
  } catch (err) {
    return {
      ok: false,
      error: normalizeBridgeError(err, GatewaySessionErrorCode.BRIDGE_ERROR),
    };
  }
}

function buildErrorLane(error) {
  return {
    available: false,
    accessToken: null,
    expiresAt: null,
    gatewayBaseUrl: null,
    installation: null,
    error: {
      code: error?.code || GatewaySessionErrorCode.GATEWAY_SESSION_ERROR,
      message: error?.message || error?.error || 'Gateway session unavailable',
    },
  };
}

function buildSuccessLane({
  identity,
  gatewayBaseUrl,
  token,
  internalShopId,
  accessToken,
  expiresAt,
}) {
  return {
    available: true,
    accessToken,
    expiresAt,
    gatewayBaseUrl,
    installation: {
      token,
      internalShopId,
      platform: String(identity.platform || 'prestashop').trim().toLowerCase(),
      moduleVersion: identity.moduleVersion || null,
      domain: identity.domain || null,
    },
    error: null,
    identity: {
      ...identity,
      gatewayBaseUrl,
      token,
      internalShopId,
    },
  };
}

async function postJson(url, body, { fetchImpl, signal } = {}) {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) {
    return {
      ok: false,
      error: {
        code: GatewaySessionErrorCode.GATEWAY_SESSION_UNAVAILABLE,
        message: 'Fetch API is not available',
      },
    };
  }

  let response;
  try {
    response = await doFetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body || {}),
      signal,
    });
  } catch (err) {
    return {
      ok: false,
      error: {
        code: GatewaySessionErrorCode.GATEWAY_SESSION_UNAVAILABLE,
        message: err?.message || 'Gateway request failed',
      },
    };
  }

  let json = null;
  try {
    json = await response.json();
  } catch (_e) {
    return {
      ok: false,
      error: {
        code: GatewaySessionErrorCode.GATEWAY_SESSION_ERROR,
        message: 'Gateway returned non-JSON response',
        httpStatus: response.status,
      },
    };
  }

  if (response.status >= 400) {
    return {
      ok: false,
      error: {
        code: json?.error?.code || json?.code || GatewaySessionErrorCode.GATEWAY_SESSION_ERROR,
        message: json?.error?.message || json?.error || json?.message || `Gateway HTTP ${response.status}`,
        httpStatus: response.status,
      },
    };
  }

  return { ok: true, data: json };
}

function validateRegisterResponse(data) {
  const installationId = isNonEmptyString(data?.installationId) ? data.installationId : '';
  const token = isNonEmptyString(data?.token) ? data.token : '';
  const secret = isNonEmptyString(data?.secret) ? data.secret : '';
  const internalShopId = isNonEmptyString(data?.internalShopId) ? data.internalShopId : '';

  if (!installationId || !token || !secret || !internalShopId) {
    return {
      ok: false,
      error: {
        code: GatewaySessionErrorCode.GATEWAY_SESSION_ERROR,
        message: 'Gateway register response missing required fields',
      },
    };
  }

  return {
    ok: true,
    data: {
      installationId,
      token,
      secret,
      internalShopId,
    },
  };
}

function validateLoginResponse(data) {
  const accessToken = data?.accessToken || data?.token || data?.access_token || '';
  if (!isNonEmptyString(accessToken)) {
    return {
      ok: false,
      error: {
        code: GatewaySessionErrorCode.GATEWAY_SESSION_ERROR,
        message: 'Gateway login response missing access token',
      },
    };
  }

  let expiresAt = null;
  const rawExpiresAt = data?.expiresAt ?? data?.expires_at ?? null;
  if (typeof rawExpiresAt === 'number' && Number.isFinite(rawExpiresAt)) {
    expiresAt = rawExpiresAt > 1e12 ? Math.floor(rawExpiresAt / 1000) : Math.floor(rawExpiresAt);
  } else if (isNonEmptyString(rawExpiresAt)) {
    const parsed = Date.parse(rawExpiresAt);
    if (Number.isFinite(parsed)) expiresAt = Math.floor(parsed / 1000);
  }

  return {
    ok: true,
    data: {
      accessToken,
      expiresAt,
      installation: data?.installation && typeof data.installation === 'object'
        ? data.installation
        : null,
    },
  };
}

function makeChallenge(domain) {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let nonce = '';
  for (let i = 0; i < bytes.length; i += 1) {
    nonce += bytes[i].toString(16).padStart(2, '0');
  }
  return { nonce, ts: Date.now(), domain };
}

async function registerIdentity(bridge, identity, gatewayBaseUrl, options) {
  const defaultLanguage = isNonEmptyString(identity.defaultLanguage)
    ? identity.defaultLanguage.trim()
    : '';
  const registerRes = await postJson(
    `${gatewayBaseUrl}/v1/installations/register`,
    {
      meta: {
        platform: identity.platform || 'prestashop',
        domain: identity.domain || '',
        moduleVersion: identity.moduleVersion || '',
        ...(defaultLanguage ? { defaultLanguage } : {}),
      },
    },
    options,
  );

  if (!registerRes.ok) return registerRes;

  const validated = validateRegisterResponse(registerRes.data);
  if (!validated.ok) return validated;

  const persistRes = await callBridge(bridge, 'gateway.installation.persist', {
    installationId: validated.data.installationId,
    token: validated.data.token,
    secret: validated.data.secret,
    internalShopId: validated.data.internalShopId,
    domain: identity.domain || '',
  });

  if (!persistRes.ok) return persistRes;

  return {
    ok: true,
    data: {
      installationId: validated.data.installationId,
      token: validated.data.token,
      internalShopId: validated.data.internalShopId,
    },
  };
}

async function ensureIdentityRegistered(bridge, identity, gatewayBaseUrl, options) {
  if (isNonEmptyString(identity.token)) {
    return {
      ok: true,
      data: {
        installationId: identity.installationId || '',
        token: identity.token,
        internalShopId: identity.internalShopId || '',
      },
    };
  }

  return registerIdentity(bridge, identity, gatewayBaseUrl, options);
}

function shouldRefreshExistingIdentity(error) {
  const code = error?.code || '';
  return code === GatewaySessionErrorCode.INSTALLATION_NOT_FOUND
    || code === GatewaySessionErrorCode.CREDENTIALS_MISSING
    || code === 'DOMAIN_MISMATCH'
    || code === 'INVALID_SIGNATURE';
}

async function loginWithChallenge(bridge, identity, gatewayBaseUrl, registered, options) {
  const challenge = makeChallenge(identity.domain || '');
  const signRes = await callBridge(bridge, 'gateway.challenge.sign', {
    token: registered.token,
    domain: challenge.domain,
    nonce: challenge.nonce,
    ts: challenge.ts,
    purpose: 'gateway.session',
  });

  if (!signRes.ok) return signRes;

  const sig = signRes.data?.sig;
  if (!isNonEmptyString(sig)) {
    return {
      ok: false,
      error: {
        code: GatewaySessionErrorCode.GATEWAY_SESSION_ERROR,
        message: 'gateway.challenge.sign response missing sig',
      },
    };
  }

  const loginRes = await postJson(
    `${gatewayBaseUrl}/v1/auth/login/challenge`,
    {
      token: registered.token,
      domain: challenge.domain,
      nonce: challenge.nonce,
      ts: challenge.ts,
      sig,
    },
    options,
  );

  if (!loginRes.ok) return loginRes;

  return validateLoginResponse(loginRes.data);
}

export async function readBrowserGatewaySessionLane(bridge, options = {}) {
  const identityRes = await callBridge(bridge, 'gateway.identity.get', {});
  if (!identityRes.ok) return buildErrorLane(identityRes.error);

  const identity = identityRes.data || {};
  const gatewayBaseUrl = normalizeBaseUrl(identity.gatewayBaseUrl) || readFallbackBaseUrl();
  if (!gatewayBaseUrl) {
    return buildErrorLane({
      code: GatewaySessionErrorCode.GATEWAY_SESSION_UNAVAILABLE,
      message: 'Gateway URL is not configured. Set window.KEA_PRO_GATEWAY_URL in admin app config.',
    });
  }
  if (!isNonEmptyString(identity.domain)) {
    return buildErrorLane({
      code: GatewaySessionErrorCode.GATEWAY_SESSION_UNAVAILABLE,
      message: 'Shop domain is not configured',
    });
  }

  const registered = await ensureIdentityRegistered(bridge, identity, gatewayBaseUrl, options);
  if (!registered.ok) return buildErrorLane(registered.error);

  let effectiveRegistered = registered;
  let login = await loginWithChallenge(bridge, identity, gatewayBaseUrl, effectiveRegistered.data, options);
  if (!login.ok
    && isNonEmptyString(identity.token)
    && shouldRefreshExistingIdentity(login.error)
  ) {
    effectiveRegistered = await registerIdentity(bridge, identity, gatewayBaseUrl, options);
    if (!effectiveRegistered.ok) return buildErrorLane(effectiveRegistered.error);
    login = await loginWithChallenge(bridge, identity, gatewayBaseUrl, effectiveRegistered.data, options);
  }
  if (!login.ok) return buildErrorLane(login.error);

  const loginInstallation = login.data.installation || {};
  const token = effectiveRegistered.data.token || loginInstallation.token || '';
  const internalShopId = effectiveRegistered.data.internalShopId || loginInstallation.internalShopId || '';

  return buildSuccessLane({
    identity,
    gatewayBaseUrl,
    token,
    internalShopId,
    accessToken: login.data.accessToken,
    expiresAt: login.data.expiresAt,
  });
}

export const __test_only = {
  callBridge,
  makeChallenge,
  normalizeBaseUrl,
  postJson,
  validateLoginResponse,
  validateRegisterResponse,
};
